import { ParticipantStatus } from "./participant.model";

export interface Appointment {
	id: string;
	cancelled: boolean;
	coachId: string; // It's not the coachID, but the coach calendarId in Acuity
	datetime: string; // Date
	type: AppointmentType;
	participant: AppointmentParticipant;
}

export interface AppointmentParticipant {
	/**
	 * ⚠️ Never present on `/appointments` or `/coach-appointments` responses.
	 *
	 * The schedule-appointments API validates those responses against
	 * `participantSchema` (packages/schemas/shared.schema.ts), which has no `id`
	 * key — Fastify strips anything not in a response schema. Use
	 * `HelperService.getParticipantRouteId(p.id, p.kannactId)` to build a
	 * `/participant/:participantId` link instead of reading this directly (KAN-2554).
	 */
	id?: number;
	kannactId: string;
	firstName: string;
	lastName: string;
	institution: string;
	timezone: string;
	email: string;
	dateOfBirth: string | undefined; // Date
	phone: string;
	isSpanish: boolean;
}

export interface AppointmentBlock {
	type: AppointmentBlockType;
	coachId: string; // Calendar ID
	datetime: string; // Date UTC: "2023-12-05T15:02:09.892Z"
	participants: { kannactId: string }[];
}

export const APPOINTMENT_TYPES_IDS = {
	KANNACT_WHOLE_HEALTH_INTRO_CALL: 44964344,
	KANNACT_INTRO_CALL: 28464617,
	TECH_SUPPORT_CALL: 5960381,
	COACHING_CALL: 49269063,
	COACHING_CALL_10: 49268835,
	COACHING_CALL_15: 11418323,
	COACHING_CALL_30: 38191966,
	COACHING_CALL_30_15: 70236055,
	COACHING_CALL_45: 66334838,
	IN_PERSON_COACHING_SESSION: 95531925,
	ZOOM_COACHING_SESSION: 95531984,
	KANNACT_WARM_HANDOFF: 58772120,
	FOLLOWUP_VISIT_20_10: 70575385,
	COVERAGE_BENEFITS_REVIEW: 72387769,
	INFORMATIONAL_CALL: 34225321,
	INFORMATION_CALL_10_5: 73990812,
	WELLNESS_CHECK_10_5: 78064877,
	WELLNESS_CHECK_20_10: 78656881,
	PREVISIT_SETUP_CALL: 86650396,
	PREVISIT_SETUP_CALL_30_10: 88669500,
	NEW_PATIENT_CONSULTATION_20: 68225585,
	NEW_PATIENT_CONSULTATION_30_15: 78971433,
	NEW_PATIENT_CONSULTATION_45: 68225627,
	NEW_PATIENT_CONSULTATION_45_PHONE: 98125281,
	FOLLOWUP_VISIT_30_10: 96514423,
	FOLLOWUP_VISIT_30_10_PHONE: 98125340,
	NURSE_VISIT: 97833719,
	PRIORITY_COACHING_CALL: 97096815,
};

export const APPOINTMENT_IDS_TYPES = {
	44964344: "KANNACT_WHOLE_HEALTH_INTRO_CALL",
	28464617: "KANNACT_INTRO_CALL",
	5960381: "TECH_SUPPORT_CALL",
	49269063: "COACHING_CALL",
	49268835: "COACHING_CALL_10",
	11418323: "COACHING_CALL_15",
	38191966: "COACHING_CALL_30",
	70236055: "COACHING_CALL_30_15",
	66334838: "COACHING_CALL_45",
	95531925: "IN_PERSON_COACHING_SESSION",
	95531984: "ZOOM_COACHING_SESSION",
	58772120: "KANNACT_WARM_HANDOFF",
	70575385: "FOLLOWUP_VISIT_20_10",
	72387769: "COVERAGE_BENEFITS_REVIEW",
	34225321: "INFORMATIONAL_CALL",
	73990812: "INFORMATION_CALL_10_5",
	78064877: "WELLNESS_CHECK_10_5",
	78656881: "WELLNESS_CHECK_20_10",
	86650396: "PREVISIT_SETUP_CALL",
	88669500: "PREVISIT_SETUP_CALL_30_10",
	68225585: "NEW_PATIENT_CONSULTATION_20",
	78971433: "NEW_PATIENT_CONSULTATION_30_15",
	68225627: "NEW_PATIENT_CONSULTATION_45",
	98125281: "NEW_PATIENT_CONSULTATION_45_PHONE",
	96514423: "FOLLOWUP_VISIT_30_10",
	98125340: "FOLLOWUP_VISIT_30_10_PHONE",
	97833719: "NURSE_VISIT",
	97096815: "PRIORITY_COACHING_CALL",
};

export enum AppointmentType {
	KANNACT_WHOLE_HEALTH_INTRO_CALL = "KANNACT_WHOLE_HEALTH_INTRO_CALL",
	KANNACT_INTRO_CALL = "KANNACT_INTRO_CALL",
	KANNACT_INTRO_CALL_10 = "KANNACT_INTRO_CALL_10",
	TECH_SUPPORT_CALL = "TECH_SUPPORT_CALL",
	COACHING_CALL = "COACHING_CALL",
	COACHING_CALL_10 = "COACHING_CALL_10",
	COACHING_CALL_15 = "COACHING_CALL_15",
	COACHING_CALL_30 = "COACHING_CALL_30",
	COACHING_CALL_30_15 = "COACHING_CALL_30_15",
	COACHING_CALL_45 = "COACHING_CALL_45",
	IN_PERSON_COACHING_SESSION = "IN_PERSON_COACHING_SESSION",
	ZOOM_COACHING_SESSION = "ZOOM_COACHING_SESSION",
	KANNACT_WARM_HANDOFF = "KANNACT_WARM_HANDOFF",
	NEW_PATIENT_CONSULTATION_20 = "NEW_PATIENT_CONSULTATION_20",
	NEW_PATIENT_CONSULTATION_30_15 = "NEW_PATIENT_CONSULTATION_30_15",
	NEW_PATIENT_CONSULTATION_45 = "NEW_PATIENT_CONSULTATION_45",
	NEW_PATIENT_CONSULTATION_45_PHONE = "NEW_PATIENT_CONSULTATION_45_PHONE",
	FOLLOWUP_VISIT_20_10 = "FOLLOWUP_VISIT_20_10",
	FOLLOWUP_VISIT_30_10 = "FOLLOWUP_VISIT_30_10",
	FOLLOWUP_VISIT_30_10_PHONE = "FOLLOWUP_VISIT_30_10_PHONE",
	COVERAGE_BENEFITS_REVIEW = "COVERAGE_BENEFITS_REVIEW",
	INFORMATIONAL_CALL = "INFORMATIONAL_CALL",
	INFORMATION_CALL_10_5 = "INFORMATION_CALL_10_5",
	WELLNESS_CHECK_10_5 = "WELLNESS_CHECK_10_5",
	WELLNESS_CHECK_20_10 = "WELLNESS_CHECK_20_10",
	PREVISIT_SETUP_CALL = "PREVISIT_SETUP_CALL",
	PREVISIT_SETUP_CALL_30_10 = "PREVISIT_SETUP_CALL_30_10",
	NURSE_VISIT = "NURSE_VISIT",
	PRIORITY_COACHING_CALL = "PRIORITY_COACHING_CALL",
}

export enum AppointmentBlockType {
	INTRO = "INTRO",
	RO = "RO",
	REENGAGE = "REENGAGE",
}

// Pre-built set for O(1) membership checks — avoids per-call Object.values()
const _BLOCK_TYPES_SET = new Set<string>(Object.values(AppointmentBlockType));

/**
 * Returns true if `type` is an AppointmentBlockType value (INTRO / RO / REENGAGE).
 * Use this instead of the old `type.includes("_BLOCK")` pattern so that block detection
 * remains correct after the enum was collapsed from per-coach strings to generic labels.
 */
export const isAppointmentBlockType = (type: string): type is AppointmentBlockType =>
	_BLOCK_TYPES_SET.has(type) || type.includes("_BLOCK");

export interface AppointmentCoach {
	id: string; // Calendar ID
	gladstoneId: string; // Coach ID in GS2
	name: string;
	bio: string;
	phone: string;
	photoUrl: string;
}

export interface AppointmentTypes {
	type: AppointmentType;
	name: string;
	durationInMinutes: number;
	paddingAfterInMinutes: number;
	paddingBeforeInMinutes: number;
	totalPaddingInMinutes: number;
}

export interface AppointmentBlocksTypes {
	type: AppointmentBlockType;
	name: string;
	durationInMinutes: number;
	paddingAfterInMinutes: number;
	paddingBeforeInMinutes: number;
	totalPaddingInMinutes: number;
}

export interface AppointmentBlockInfo {
	id: number;
	kannactId: string;
	firstName: string;
	lastName: string;
	status: ParticipantStatus;
	phoneNumber: string;
}
